/**
 * AdminAgentLogTab (on top of Feature A4)
 * =======================================
 * Enterprise dashboard for the per-step LLM audit log.
 *
 * Features
 * --------
 * • Token-spend summary by (client × operation × model) with call/error
 *   counts and avg latency.
 * • Filterable log viewer (operation / simulation_id / client_id / status).
 * • Pagination via the Sprint-1 `{items,total,page,page_size}` envelope.
 * • Click any row to inspect full record incl. prompt_hash.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { TabsContent } from '../ui/tabs';
import {
  Activity, AlertTriangle, CheckCircle2, Coins, RefreshCw, Timer, X,
} from 'lucide-react';
import { toast } from 'sonner';

const PAGE_SIZE = 25;

const formatNumber = (n) => {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US').format(n);
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function AdminAgentLogTab() {
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);

  const [fOp, setFOp] = useState('');
  const [fSimId, setFSimId] = useState('');
  const [fClientId, setFClientId] = useState('');
  const [fStatus, setFStatus] = useState('all');

  const [selected, setSelected] = useState(null);

  const loadSummary = async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const r = await api.get('/admin/agent-log/summary');
      setSummary(r.data?.rows || []);
    } catch (err) {
      setSummaryError(err.response?.data?.detail || 'Could not load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadList = async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      if (fOp) params.set('operation', fOp);
      if (fSimId) params.set('simulation_id', fSimId);
      if (fClientId) params.set('client_id', fClientId);
      if (fStatus && fStatus !== 'all') params.set('status', fStatus);
      const r = await api.get(`/admin/agent-log?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load log');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadSummary(); }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [page, fOp, fSimId, fClientId, fStatus]);

  const totals = useMemo(() => {
    const acc = summary.reduce(
      (a, r) => {
        a.calls += r.calls || 0;
        a.errors += r.errors || 0;
        a.input += r.input_tokens || 0;
        a.output += r.output_tokens || 0;
        a.total += r.total_tokens || 0;
        return a;
      },
      { calls: 0, errors: 0, input: 0, output: 0, total: 0 }
    );
    return acc;
  }, [summary]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleClearFilters = () => {
    setFOp(''); setFSimId(''); setFClientId(''); setFStatus('all'); setPage(1);
  };

  return (
    <TabsContent value="agent-log" className="space-y-6" data-testid="admin-agent-log-tab">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Activity className="w-5 h-5 text-amber-500 mt-0.5" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Agent Audit Log</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Every LLM call, retrieval, and tool invocation — captured for compliance,
              replayability, and cost attribution.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadSummary(); loadList(); }} data-testid="agent-log-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Activity,    color: 'text-blue-600',    bg: 'bg-blue-50',    label: 'Total calls',  value: formatNumber(totals.calls) },
          { icon: AlertTriangle, color: 'text-red-600',  bg: 'bg-red-50',    label: 'Errors',       value: formatNumber(totals.errors) },
          { icon: Coins,       color: 'text-amber-600',   bg: 'bg-amber-50',   label: 'Total tokens', value: formatNumber(totals.total) },
          { icon: Timer,       color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Output / Input',
            value: `${formatNumber(totals.output)} / ${formatNumber(totals.input)}` },
        ].map((k) => (
          <Card key={k.label} className={`${k.bg} border-transparent`} data-testid={`agent-log-kpi-${k.label.replace(/\s+/g, '-').toLowerCase()}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <k.icon className={`w-5 h-5 ${k.color}`} />
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k.label}</div>
                <div className={`text-lg font-semibold ${k.color} tabular-nums`}>{k.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Spend summary table */}
      <Card data-testid="agent-log-summary-card">
        <CardHeader>
          <CardTitle className="text-base">Token spend by client / operation / model</CardTitle>
          <CardDescription>Top 200 combinations, sorted by total_tokens descending.</CardDescription>
        </CardHeader>
        <CardContent>
          {summaryError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">
              {summaryError}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b">
                <tr className="[&>th]:text-left [&>th]:font-medium [&>th]:py-2 [&>th]:px-2">
                  <th>Client</th>
                  <th>Operation</th>
                  <th>Model</th>
                  <th className="!text-right">Calls</th>
                  <th className="!text-right">Errors</th>
                  <th className="!text-right">In tokens</th>
                  <th className="!text-right">Out tokens</th>
                  <th className="!text-right">Total</th>
                  <th className="!text-right">Avg latency (ms)</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && !summaryLoading && (
                  <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No agent-log data yet. Run a simulation or interview to populate.</td></tr>
                )}
                {summary.map((r, i) => (
                  <tr key={i} className="border-b last:border-0 [&>td]:py-2 [&>td]:px-2 hover:bg-slate-50">
                    <td className="font-mono text-[11px] truncate max-w-[160px]" title={r.client_id || '—'}>{r.client_id || '—'}</td>
                    <td><Badge variant="secondary" className="text-[10px]">{r.operation || '—'}</Badge></td>
                    <td className="text-muted-foreground">{r.model || '—'}</td>
                    <td className="text-right tabular-nums">{formatNumber(r.calls)}</td>
                    <td className={`text-right tabular-nums ${r.errors ? 'text-red-600 font-medium' : ''}`}>{formatNumber(r.errors)}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{formatNumber(r.input_tokens)}</td>
                    <td className="text-right tabular-nums text-muted-foreground">{formatNumber(r.output_tokens)}</td>
                    <td className="text-right tabular-nums font-semibold">{formatNumber(r.total_tokens)}</td>
                    <td className="text-right tabular-nums">{r.avg_latency_ms?.toFixed?.(0) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filterable log viewer */}
      <Card data-testid="agent-log-list-card">
        <CardHeader>
          <CardTitle className="text-base">Log entries</CardTitle>
          <CardDescription>Most recent first. Click any row to view details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-[11px]">Operation</Label>
              <Input value={fOp} onChange={(e) => { setFOp(e.target.value); setPage(1); }} placeholder="interview.single" data-testid="agent-log-filter-op" />
            </div>
            <div>
              <Label className="text-[11px]">Simulation ID</Label>
              <Input value={fSimId} onChange={(e) => { setFSimId(e.target.value); setPage(1); }} placeholder="seed-sim-..." data-testid="agent-log-filter-sim" />
            </div>
            <div>
              <Label className="text-[11px]">Client ID</Label>
              <Input value={fClientId} onChange={(e) => { setFClientId(e.target.value); setPage(1); }} placeholder="client uuid" data-testid="agent-log-filter-client" />
            </div>
            <div>
              <Label className="text-[11px]">Status</Label>
              <Select value={fStatus} onValueChange={(v) => { setFStatus(v); setPage(1); }}>
                <SelectTrigger data-testid="agent-log-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={handleClearFilters} data-testid="agent-log-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" />Clear filters
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b">
                <tr className="[&>th]:text-left [&>th]:font-medium [&>th]:py-2 [&>th]:px-2">
                  <th>Time</th>
                  <th>Operation</th>
                  <th>Model</th>
                  <th>Simulation</th>
                  <th className="!text-right">Tokens</th>
                  <th className="!text-right">Latency</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && !listLoading && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No entries match current filters.</td></tr>
                )}
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-b last:border-0 [&>td]:py-2 [&>td]:px-2 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(it)}
                    data-testid={`agent-log-row-${it.id}`}
                  >
                    <td className="whitespace-nowrap">{timeAgo(it.timestamp)}</td>
                    <td><Badge variant="secondary" className="text-[10px]">{it.operation}</Badge></td>
                    <td className="text-muted-foreground">{it.model}</td>
                    <td className="font-mono text-[11px] truncate max-w-[180px]" title={it.simulation_id || ''}>{it.simulation_id || '—'}</td>
                    <td className="text-right tabular-nums">{formatNumber(it.total_tokens)}</td>
                    <td className="text-right tabular-nums">{it.latency_ms ?? '—'}</td>
                    <td>
                      {it.status === 'success' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-[11px]"><CheckCircle2 className="w-3 h-3" />success</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 text-[11px]"><AlertTriangle className="w-3 h-3" />error</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-muted-foreground">
              {total ? `${(page - 1) * PAGE_SIZE + 1} – ${Math.min(page * PAGE_SIZE, total)} of ${formatNumber(total)}` : '0 entries'}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1 || listLoading} onClick={() => setPage((p) => p - 1)} data-testid="agent-log-prev-page">Prev</Button>
              <span className="text-xs px-2">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages || listLoading} onClick={() => setPage((p) => p + 1)} data-testid="agent-log-next-page">Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail panel */}
      {selected && (
        <Card data-testid="agent-log-detail-card">
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Log entry detail</CardTitle>
              <CardDescription className="font-mono text-[11px] break-all">{selected.id}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="agent-log-detail-close">
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-950 text-slate-100 text-[11px] rounded p-3 overflow-x-auto max-h-80">
{JSON.stringify(selected, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </TabsContent>
  );
}
